<script generic="T extends Record<string, any> | any" lang="ts" setup>
import type { NovoIconNames } from '@novonordisk/studyhub-novoui/vue'
import { NIcon } from '@novonordisk/studyhub-novoui/vue'
import { useVirtualList } from '@vueuse/core'
import { computed, ref } from 'vue'

const {
  items,
  itemText = undefined,
  itemValue = undefined,
  itemDisabled = undefined,
  itemDisabledTooltipPlacement = 'left',
  itemDisabledTooltipText = 'You cannot choose this option.',
  filterFn = undefined,
  searchPlaceholder = undefined,
  noResultsPlaceholder = 'No results.',
  placeholder = undefined,
  messages = [],
  errorMessages = [],
  invalid = undefined,
  disabled = false,
  readonly = false,
  label = undefined,
  disableSearch = false,
  loading = false,
  prependIcon = 'chevronDown',
  required = false,
  size = 'medium',
  multiple = false,
  clearable = false,
  popoverWidth = undefined,
  variant = 'default',
} = defineProps<{
  items: T[]
  itemText?: keyof T extends string ? keyof T : never
  itemValue?: keyof T extends string ? keyof T : never
  itemDisabled?: (item: T) => boolean
  itemDisabledTooltipText?: string
  itemDisabledTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  filterFn?: (items: T[]) => T[]
  placeholder?: string
  searchPlaceholder?: string
  noResultsPlaceholder?: string
  messages?: string[]
  errorMessages?: string[]
  invalid?: boolean
  disabled?: boolean
  readonly?: boolean
  label?: string
  disableSearch?: boolean
  loading?: boolean
  prependIcon?: NovoIconNames
  required?: boolean
  size?: 'small' | 'medium'
  multiple?: boolean
  clearable?: boolean
  popoverWidth?: string
  variant?: 'default' | 'light'
}>()

defineSlots<{
  'item:prepend': { item: T; isSelected: boolean }
  'value:prepend': { item: T | T[] }
  'item:append': (scope: { item: T }) => void
  activator: (scope: { active: boolean; activate: () => void }) => void
}>()

const model = defineModel<unknown>()

const popoverActive = defineModel<boolean>('popoverActive', { default: false })
const search = ref<string>('')
const hoveredIndex = ref<number | null>(null)
const searchElement = ref<HTMLInputElement | undefined>()

const filter = (items: T[]): T[] => {
  if (filterFn) {
    return filterFn(items)
  }

  return items.filter(item => {
    if (!search.value || search.value === '') {
      return true
    }

    return getItemText(item)?.toString().toLowerCase().includes(search.value.toLowerCase())
  })
}

const filteredItems = computed((): T[] => {
  return filter(items)
})

const { list, containerProps, wrapperProps } = useVirtualList(filteredItems, {
  itemHeight: 40,
})

const searchWords = computed(() => {
  return search.value.split(' ')
})

const hasErrors = computed((): boolean => {
  return !disabled && !!(invalid || (errorMessages && errorMessages.length))
})

const hasErrorMessages = computed(() => {
  return !disabled && errorMessages && errorMessages.length
})

const hasMessages = computed(() => {
  return !hasErrors.value && messages && messages.length
})

const modelObject = computed(() => {
  if (multiple && Array.isArray(model.value)) {
    return model.value.map(value => {
      const item = items.find(item => value == getItemValue(item))
      return item || null
    })
  }

  return items.find(item => model.value == getItemValue(item)) || null
})

const modelText = computed(() => {
  if (multiple && Array.isArray(model.value)) {
    return model.value
      .map(value => {
        const item = items.find(item => value == getItemValue(item))
        return item ? getItemText(item) : ''
      })
      .join(', ')
  }

  const item = items.find(item => model.value == getItemValue(item))

  if (!item) {
    return null
  }

  return getItemText(item)
})

function getItemText(item: T): string {
  if (itemText && item && typeof item === 'object' && itemText in item) {
    return item[itemText] as string
  }

  return item as string
}

function getItemValue(item: T): string {
  if (itemValue && item && typeof item === 'object' && itemValue in item) {
    return item[itemValue] as string
  }

  return item as string
}

function selectItem(item: T) {
  if (itemDisabled && itemDisabled(item)) {
    return
  }

  if (multiple) {
    if (!model.value) {
      model.value = [getItemValue(item)]
    } else if (Array.isArray(model.value)) {
      if (model.value.includes(getItemValue(item))) {
        model.value = model.value.filter(value => value !== getItemValue(item))
      } else {
        model.value = [...model.value, getItemValue(item)]
      }
    }
  } else {
    model.value = getItemValue(item)
    popoverActive.value = false
  }
}

function isItemSelected(item: T) {
  if (multiple && Array.isArray(model.value)) {
    return model.value.includes(getItemValue(item))
  }

  return model.value == getItemValue(item)
}

function setSelectedIndex(index: number | null) {
  hoveredIndex.value = index
}

function incrementSelectedIndex() {
  if (hoveredIndex.value === null) {
    hoveredIndex.value = 0
  } else if (hoveredIndex.value === filteredItems.value.length - 1) {
    return
  } else {
    hoveredIndex.value += 1
  }
  document.getElementById(`v-search-field-item-${hoveredIndex.value}`)?.scrollIntoView({
    block: 'nearest',
  })
}

function decrementSelectedIndex() {
  if (hoveredIndex.value === null || hoveredIndex.value === 0) {
    return
  } else {
    hoveredIndex.value -= 1
  }
  document.getElementById(`v-search-field-item-${hoveredIndex.value}`)?.scrollIntoView({
    block: 'nearest',
  })
}

function chooseSelectedIndex() {
  if (hoveredIndex.value !== null) {
    selectItem(filteredItems.value[hoveredIndex.value])
  }
}

function onInputUpdated() {
  setSelectedIndex(null)
  containerProps.ref.value?.scrollTo({
    top: 0,
  })
}

async function onActiveUpdated(value: boolean) {
  if (value) {
    search.value = ''
    await new Promise(resolve => setTimeout(resolve, 10))
    searchElement.value?.focus()
  }
}
</script>

<template>
  <div data-test="v-select">
    <VInputLabel
      v-if="label"
      :disabled="disabled"
      :has-errors="hasErrors"
      data-test="v-select-label"
    >
      <VAsterisk :active="required">
        {{ label }}
      </VAsterisk>
    </VInputLabel>
    <VPopover
      v-model="popoverActive"
      :disabled="disabled || readonly"
      :width="popoverWidth"
      data-test="v-select-popover"
      @update:model-value="onActiveUpdated"
    >
      <template #activator="{ active, activate }">
        <slot
          name="activator"
          :active="active"
          :activate="activate"
        >
          <VInputActivator
            :active="active"
            :disabled="disabled"
            :has-errors="hasErrors"
            :loading="loading"
            :size="size"
            :icon="prependIcon"
            :clearable="clearable"
            :value="model"
            :variant="variant"
            data-test="v-select-activator"
            @click="loading ? () => {} : activate()"
            @click:clear="() => (multiple ? (model = []) : (model = null))"
          >
            <div
              class="flex min-w-0 items-center gap-2"
              data-test="v-select-value"
            >
              <slot
                name="value:prepend"
                :item="modelObject"
              />
              <VInputActivatorValue
                :has-errors="hasErrors"
                :model-text="modelText"
                :disabled="disabled"
                data-test="v-select-value"
              >
                {{ modelText || placeholder }}
              </VInputActivatorValue>
            </div>
          </VInputActivator>
        </slot>
      </template>
      <VPopoverScroll class="px-0!">
        <div
          v-if="!disableSearch"
          class="relative px-4 py-2"
          data-test="v-select-search-container"
        >
          <input
            ref="searchElement"
            v-model="search"
            :placeholder="searchPlaceholder"
            class="border-general-medium bg-general-subtle text-general-primary ring-primary hover:border-general-high-emphasis-hover focus:border-general-focus focus:bg-general-medium-emphasis-hover flex h-12 w-full justify-between rounded border py-3 pr-4 pl-[40px] text-left transition-colors duration-100 ease-in-out ring-inset focus:ring-1 focus:outline-0"
            :class="{
              'text-ui-input-14 h-10!': size === 'small',
              'text-ui-input-16 h-12': size === 'medium',
            }"
            data-test="v-select-search-input"
            @input="onInputUpdated"
            @keydown.prevent.stop.down="incrementSelectedIndex"
            @keydown.prevent.stop.up="decrementSelectedIndex"
            @keydown.prevent.stop.enter="chooseSelectedIndex"
            @keydown.prevent.stop.esc="popoverActive = false"
          />
          <NIcon
            class="fill fill-general-secondary absolute top-1/2 left-8 -translate-y-1/2"
            icon="search"
            variant="small"
            :size="16"
          />
        </div>
        <VDivider
          v-if="!disableSearch"
          class="my-2"
        />
        <div
          v-if="filteredItems.length"
          data-test="v-select-items-container"
        >
          <div
            class="max-h-[200px] min-h-[120px] overflow-y-auto"
            v-bind="containerProps"
          >
            <div v-bind="wrapperProps">
              <VSelectOption
                v-for="item in list"
                :id="`v-search-field-item-${item.index}`"
                :key="getItemValue(item.data) ?? item.index"
                :is-hovered="hoveredIndex === item.index"
                :is-selected="isItemSelected(item.data)"
                :is-disabled="itemDisabled ? itemDisabled(item.data) : false"
                :item-data="item.data"
                :item-text="getItemText(item.data)"
                :search-words="searchWords"
                :size="size"
                :tooltip-placement="itemDisabledTooltipPlacement"
                :tooltip-text="itemDisabled && itemDisabled(item.data) ? itemDisabledTooltipText : undefined"
                data-test="v-select-option"
                @click="selectItem(item.data)"
                @mouseenter="setSelectedIndex(item.index)"
              >
                <template #prepend>
                  <slot
                    :item="item.data"
                    :is-selected="isItemSelected(item.data)"
                    name="item:prepend"
                  />
                </template>
                <template #append>
                  <slot
                    :item="item.data"
                    name="item:append"
                  />
                </template>
              </VSelectOption>
            </div>
          </div>
        </div>
        <VGroupSelectNoResults
          v-else
          data-test="v-select-no-results"
        >
          {{ noResultsPlaceholder }}
        </VGroupSelectNoResults>
      </VPopoverScroll>
    </VPopover>
    <VInputFeedback
      v-if="hasErrorMessages"
      :messages="errorMessages"
      type="error"
      data-test="v-select-error-feedback"
    />
    <VInputFeedback
      v-else-if="hasMessages"
      :messages="messages"
      type="info"
      data-test="v-select-info-feedback"
    />
  </div>
</template>
